import { inject, Injectable } from "@angular/core";
import { SyncStatusEnum } from "../../../../models/types/general-types";
import { AccountView } from "../../../../models/view/account/account.view";
import { GlossSyncService } from "../../gloss.sync.service";
import { BasiqTransactionValidator } from "../validators/basiq.validator.transaction";
import { BasiqTransactionAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.transaction";
import { BasiqMapper } from "@bitwarden/web-vault/app/services/syncing/basiq/mappers/basiq.mapper";
import { BasiqCategoryAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.category";
import { BasiqCategoryMapper } from "@bitwarden/web-vault/app/services/syncing/basiq/mappers/basiq.mapper.category";

@Injectable({
  providedIn: "root",
})
export class BasiqAccountSync {
  constructor(
    private glossSyncService: GlossSyncService = inject(GlossSyncService),
    private basiqTransactionValidator: BasiqTransactionValidator = inject(
      BasiqTransactionValidator,
    ),
    private basiqTransactionAction: BasiqTransactionAction = inject(BasiqTransactionAction),
    private basiqCategoryAction: BasiqCategoryAction = inject(BasiqCategoryAction),
    private basiqMapper: BasiqMapper = inject(BasiqMapper),
    private basiqCategoryMapper: BasiqCategoryMapper = inject(BasiqCategoryMapper),
  ) {}

  async syncAccount(account: AccountView) {
    try {
      if (!(await this.basiqTransactionAction.pullTransactions(account))) {
        await this.glossSyncService.terminateSync(
          [account],
          this.basiqTransactionAction.syncStatus,
        );
        return;
      }

      const rawTransactions = this.basiqTransactionAction.rawTransactions.data;
      if (rawTransactions.length === 0) {
        await this.glossSyncService.terminateSync([account], SyncStatusEnum.synced);
        return;
      }

      const categories = await this.basiqCategoryMapper.mapCategories(account, rawTransactions);
      if (
        !(await this.basiqMapper.mapTransactionData(
          rawTransactions,
          account,
          this.basiqCategoryMapper.transactionCategoryMap,
          this.basiqCategoryAction,
        ))
      ) {
        await this.glossSyncService.terminateSync([account], this.basiqMapper.syncStatus);
        return;
      }

      if (!(await this.basiqCategoryAction.saveCategories(categories, account))) {
        await this.glossSyncService.terminateSync([account], this.basiqCategoryAction.syncStatus);
        return;
      }

      const transactions = this.basiqMapper.mappedBasiqData.transactions;
      if (!(await this.basiqTransactionValidator.validate(transactions, account))) {
        await this.glossSyncService.terminateSync(
          [account],
          this.basiqTransactionValidator.syncStatus,
        );
        return;
      }

      const validatedTransactions = this.basiqTransactionValidator.validatedTransactions;
      const mappedSourceTransactions = this.basiqMapper.mappedBasiqData.sourceTransactions;
      if (
        !(await this.basiqTransactionAction.saveSourceTransactions(
          validatedTransactions,
          mappedSourceTransactions,
          account,
        ))
      ) {
        await this.glossSyncService.terminateSync(
          [account],
          this.basiqTransactionAction.syncStatus,
        );
        return;
      }

      if (!(await this.basiqTransactionAction.saveTransactions(validatedTransactions, account))) {
        await this.glossSyncService.terminateSync(
          [account],
          this.basiqTransactionAction.syncStatus,
        );
        return;
      }

      if (!(await this.basiqTransactionAction.saveCBAT(account))) {
        await this.glossSyncService.terminateSync(
          [account],
          this.basiqTransactionAction.syncStatus,
        );
        return;
      }

      await this.glossSyncService.terminateSync([account], SyncStatusEnum.synced);
    } catch (error) {
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError(
        [account],
        error,
        "syncing account",
        SyncStatusEnum.unknownError,
      );
      await this.glossSyncService.terminateSync([account], SyncStatusEnum.unknownError, error);
    }
  }
}
